import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import Button from "components/Button";
import {
  Board,
  TitleArea,
  Title,
  Table,
  Heading,
  Row,
  FieldValue,
  FieldName,
  CenterFieldValue,
} from "components/LayoutParts";
import Loading from "components/Loading";
import Modal from "components/Modal";
import SearchForm from "components/SearchForm";
import { useDebounce } from "hooks/useDebounce";
import { useFindUsers } from "pages/AtPort/api/useFindUsers";
import AtPortSelect from "pages/AtPort/component/AtPortSelect";
import PaginationForAtPort from "pages/AtPort/component/PaginationForAtPort";
import CreateUser from "pages/AtPort/User/CreateUser";
import { jumpUpToElement } from "utils/animationUtils";

// パーツ定義
const ActionArea = styled.div`
  display: flex;
  flex-direction: row;
  margin: 20px 0;
  gap: 12px;
`;
const Wrap = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: auto;
  gap: 10px;
`;

// 本体
const User = () => {
  const navigate = useNavigate();
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isModalShow, setIsModalShow] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pageOption, setPageOption] = useState<AtPortPageOption>({ after: "", before: "", pageIndex: 1 });
  const [searchTarget, setSearchTarget] = useState<{
    key?: number | string;
    label: string;
    value: string;
    disabled?: boolean;
  } | null>(null);
  const { debouncedValue: debouncedSearchKeyword } = useDebounce(searchKeyword, 500);
  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  };
  const hideModal = () => setIsModalShow(false);
  const showModal = () => setIsModalShow(true);
  const navigateBulkInsert = () => navigate("/at_port/user/bulk_insert");

  const { data, pagination, mutate } = useFindUsers({
    searchKeyword: debouncedSearchKeyword && !!searchTarget ? debouncedSearchKeyword : "",
    first: pageOption.first,
    after: pageOption.after,
    before: pageOption.before,
    last: pageOption.last,
    searchTarget:
      searchTarget && searchKeyword
        ? (searchTarget?.value as "accountId" | "contactId" | "companyName" | "email")
        : undefined,
  });

  const onClickRow = async (id: string) => {
    navigate(`/at_port/user/${id}`);
  };

  const refetch = async () => {
    await mutate();
  };

  useEffect(() => {
    setTotalPages(pagination?.totalPages ?? 1);
    return () => {
      jumpUpToElement("root");
    };
  }, [data, pagination]);

  return (
    <>
      <Board>
        <TitleArea>
          <Title>アカウント一覧</Title>
          <Wrap>
            <Button type="primary" onClick={showModal} label="新規作成" width="160px" />
            <Button type="primary" onClick={navigateBulkInsert} label="CSV一括新規登録" width="200px" />
          </Wrap>
        </TitleArea>
        <ActionArea>
          <AtPortSelect
            choices={[
              { key: "accountId", label: "顧客ID", value: "accountId" },
              { key: "companyName", label: "顧客名", value: "companyName" },
              { key: "contactId", label: "担当者ID", value: "contactId" },
              { key: "email", label: "担当者メールアドレス", value: "email" },
            ]}
            placeholder="絞り込み"
            width="200px"
            value={searchTarget}
            onChange={(value) => setSearchTarget(value)}
          />
          <SearchForm
            readOnly={!searchTarget}
            width="350px"
            value={searchKeyword}
            placeholder="検索"
            onChange={onSearchChange}
          />
        </ActionArea>
        <Table>
          <Heading>
            <FieldName flex={1}>顧客ID</FieldName>
            <FieldName flex={1}>顧客名</FieldName>
            <FieldName flex={1}>担当者ID</FieldName>
            <FieldName flex={1}>担当者メールアドレス</FieldName>
          </Heading>
          {data ? (
            data.length === 0 ? (
              <Row>
                <CenterFieldValue flex={1}>該当データがありません</CenterFieldValue>
              </Row>
            ) : (
              data.map((user) => (
                <Row key={user.contactId} onClick={() => onClickRow(user.id)}>
                  <FieldValue flex={1}>{user.accountId}</FieldValue>
                  <FieldValue flex={1}>{user.companyName}</FieldValue>
                  <FieldValue flex={1}>{user.contactId}</FieldValue>
                  <FieldValue flex={1}>{user.email}</FieldValue>
                </Row>
              ))
            )
          ) : (
            <Loading />
          )}
        </Table>
        <PaginationForAtPort
          endCursor={pagination?.endCursor ?? ""}
          startCursor={pagination?.startCursor ?? ""}
          hasNextPage={pagination?.hasNextPage ?? false}
          hasPreviousPage={pagination?.hasPreviousPage ?? false}
          pageOption={pageOption}
          totalPageCount={totalPages}
          setPageOption={setPageOption}
        />
      </Board>
      <Modal isVisible={isModalShow} onClickOutside={hideModal}>
        <CreateUser fetch={refetch} />
      </Modal>
    </>
  );
};

export default User;
